import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, X, Check, Play, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StockVideoPicker — Modal zur Auswahl eines Loop-Videos aus der kuratierten Pexels-Library.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onSelect: (video) => void  — video = {id, title, url, thumbnail, duration}
 *   industryHint: string  — initiale Branche zum Pre-Filter
 */
export function StockVideoPicker({ open, onClose, onSelect, industryHint = "" }) {
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState(industryHint);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hovering, setHovering] = useState(null);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => { setIndustry(industryHint); }, [industryHint]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/stock-videos`, {
          params: { q: query, industry, limit: 24 },
          withCredentials: true,
        });
        setResults(res.data || []);
      } catch (e) {
        toast.error("Video-Library konnte nicht geladen werden");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, industry, open]);

  useEffect(() => {
    const handler = e => { if (e.key === "Escape" && open) onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const apply = () => {
    if (!selected) { toast.error("Bitte erst ein Video anklicken"); return; }
    onSelect?.(selected);
    onClose?.();
  };

  const INDUSTRY_CHIPS = [
    { k: "", label: "Alle" },
    { k: "handwerk", label: "Handwerk" },
    { k: "taxi", label: "Taxi/Transport" },
    { k: "friseur", label: "Friseur/Beauty" },
    { k: "restaurant", label: "Restaurant" },
    { k: "fitness", label: "Fitness" },
    { k: "technologie", label: "Tech/IT" },
    { k: "business", label: "Business" },
    { k: "medizin", label: "Medizin" },
    { k: "allgemein", label: "Abstrakt/Allgemein" },
  ];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stock-video-picker-overlay"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-card border rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="stock-video-picker-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold">Stock-Video auswählen</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Kuratierte Loop-Videos (Pexels). Klicke ein Video zur Auswahl.</p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-md hover:bg-muted flex items-center justify-center" data-testid="close-video-picker"><X size={16} /></button>
        </div>

        {/* Filter-Bar */}
        <div className="px-6 py-3 border-b space-y-3 bg-muted/20">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Suche z.B. „Kochen“, „Skyline“, „Yoga“ …"
              className="pl-9 h-9"
              data-testid="video-search-input"
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {INDUSTRY_CHIPS.map(c => (
              <button
                key={c.k || "all"}
                onClick={() => setIndustry(c.k)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  industry === c.k
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background hover:bg-muted border-border"
                }`}
                data-testid={`industry-chip-${c.k || "all"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-video rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="text-center py-16 text-sm text-muted-foreground">Keine Videos gefunden. Versuch eine andere Suche.</div>
          )}
          {!loading && results.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="video-grid">
              {results.map(v => {
                const isSel = selected?.id === v.id;
                const isHov = hovering === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelected(v)}
                    onMouseEnter={() => setHovering(v.id)}
                    onMouseLeave={() => setHovering(null)}
                    className={`group relative aspect-video rounded-md overflow-hidden border-2 transition ${
                      isSel ? "border-primary ring-2 ring-primary/40" : "border-transparent hover:border-primary/60"
                    }`}
                    data-testid={`video-card-${v.id}`}
                  >
                    {/* Thumbnail — on error: graceful CSS placeholder (gradient + play-icon) */}
                    <div
                      className="absolute inset-0 w-full h-full"
                      style={{
                        background: `linear-gradient(135deg, hsl(${(v.id.length * 37) % 360} 45% 28%), hsl(${(v.id.length * 37 + 60) % 360} 45% 18%))`,
                      }}
                    />
                    <img
                      src={v.thumbnail}
                      alt={v.title}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity ${isHov ? "opacity-0" : "opacity-100"}`}
                      loading="lazy"
                      onError={e => { e.currentTarget.style.display = "none"; }}
                    />
                    {/* Hover: echtes Video */}
                    {isHov && (
                      <video
                        src={v.url}
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    )}
                    {/* Overlay-Scrim + Meta */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex flex-col justify-end p-3 pointer-events-none">
                      <div className="text-left">
                        <div className="text-xs font-semibold text-white drop-shadow">{v.title}</div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-white/75">
                          <Clock size={10} /> {v.duration}s
                        </div>
                      </div>
                    </div>
                    {/* Selected checkmark */}
                    {isSel && (
                      <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                        <Check size={13} strokeWidth={3} />
                      </div>
                    )}
                    {/* Hover-Play-Icon */}
                    {!isHov && !isSel && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none">
                        <div className="h-10 w-10 rounded-full bg-white/90 text-black flex items-center justify-center shadow-xl">
                          <Play size={16} fill="currentColor" />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-between gap-4 bg-background">
          <div className="text-xs text-muted-foreground truncate min-w-0 flex-1">
            {selected ? <>Ausgewählt: <span className="font-medium text-foreground">{selected.title}</span></> : <>Klicke ein Video — dann &bdquo;Übernehmen&ldquo;.</>}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="cancel-video-picker">Abbrechen</Button>
            <Button size="sm" disabled={!selected} onClick={apply} data-testid="apply-video-picker">
              Übernehmen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StockVideoPicker;
